/**
 * The principal's app registry as the last successful `getApps` call answered it (layered shell
 * surfaces, WS-6 §3.3, review finding architecture-1).
 *
 * The loader host resolves every mount against this one copy, and the interim root's
 * `AppRegistryProvider` fills it through its own `getApps` call, so the pane and the host read the
 * same rows: an app the pane lists resolves, at the version the pane shows. One module instance
 * serves both, because the loader chunk holds this module and the interim root imports it from
 * there. No I/O. The two module variables below are its only state: the snapshot and the change
 * listeners of {@link subscribeAppRegistry}.
 */
import type { AppRegistryEntry } from './types.js';

const EMPTY: readonly AppRegistryEntry[] = Object.freeze([]);

let latest: readonly AppRegistryEntry[] = EMPTY;

const listeners = new Set<() => void>();

/**
 * Call every change listener once, in subscription order. A listener that throws is skipped and
 * the next one runs, so a record or a forget never throws (surface extension points, WS-7).
 */
function notify(): void {
  for (const listener of Array.from(listeners)) {
    try {
      listener();
    } catch {
      // A listener's fault stays with the listener: the snapshot already changed for every reader.
    }
  }
}

/**
 * Listen to snapshot changes (surface extension points, D10, WS-7).
 *
 * Callers: the shell's extension point source (`createShellExtensionPointSource` in
 * `boot/shell-services.ts`), so that `services.extensions` of every mount hears a registry refetch.
 *
 * `listener` runs synchronously after each {@link recordAppRegistry}, an empty answer included.
 * It also runs after each {@link forgetAppRegistry} that replaced a recorded answer: rows, or a
 * recorded empty answer. So `recordAppRegistry([])` and then a forget run it twice, and the
 * snapshot reads empty both times. A forget of a snapshot that nothing recorded since the last
 * forget runs nothing. The new snapshot is in place when `listener` runs. A throw from `listener`
 * never reaches the writer. Each call adds one entry, so the same
 * function subscribed twice runs twice. Returns an idempotent unsubscribe: after it the listener
 * never runs again. Never throws.
 */
export function subscribeAppRegistry(listener: () => void): () => void {
  const entry = (): void => listener();
  listeners.add(entry);
  return () => {
    listeners.delete(entry);
  };
}

/** The last recorded registry, frozen; `[]` before the first record and after `forgetAppRegistry`. Never throws. */
export function appRegistrySnapshot(): readonly AppRegistryEntry[] {
  return latest;
}

/**
 * Replace the snapshot with a frozen copy of `apps`. `getApps` calls it with every successful
 * answer, before its promise resolves, so a caller that renders the rows renders nothing the
 * host cannot resolve. A failed read records nothing. Every record then runs the change listeners
 * of {@link subscribeAppRegistry}. Never throws.
 */
export function recordAppRegistry(apps: readonly AppRegistryEntry[]): void {
  latest = Object.freeze([...apps]);
  notify();
}

/**
 * Reset the snapshot to `[]`. The loader calls it when its own registry read fails, so no row an
 * earlier answer held resolves after that: the fail-closed direction. Idempotent; never throws.
 * The change listeners run only when the snapshot held rows or a recorded empty answer before
 * the call, as {@link subscribeAppRegistry} states; a forget of an already forgotten snapshot
 * tells nobody.
 */
export function forgetAppRegistry(): void {
  if (latest === EMPTY) return;
  latest = EMPTY;
  notify();
}
